import { Box, Button } from '@factorialco/deprecated-design-system'
import { EmployeePulseItemFragment } from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import TextArea from 'modules/core/components/TextArea'
import { useEffect } from 'modules/core/hooks/useEffect'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { Result, UnhandledResult, handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { MoodSelector } from 'modules/engagement/components/MoodSelector'
import { useEmployeePulseQuestion } from 'modules/engagement/hooks/useEmployeePulseQuestion'

interface NewEmployeePulseFormValues {
  value: number
  comment?: string
}

interface Props {
  onSubmit: (data: NewEmployeePulseFormValues) => Promise<UnhandledResult<Result<unknown>>>
  onClose: () => void
  source: 'dashboard' | 'employee_profile' | 'clock_out'
  employeePulse?: EmployeePulseItemFragment | null
}

export function NewEmployeePulseModal({ onSubmit, onClose, employeePulse, source }: Props) {
  const { pulseQuestion, loading } = useEmployeePulseQuestion()

  const form = useForm<NewEmployeePulseFormValues>({
    defaultValues: {
      value: employeePulse?.value || -1,
      comment: employeePulse?.comment || undefined,
    },
    validate: (data) => {
      const errors: FormErrors<NewEmployeePulseFormValues> = {}

      if (data.value < 1) {
        errors.value = i18n.t('common.validations.empty')
      }

      return errors
    },
    onSubmit: async (data, { setErrors }) => {
      const result = await onSubmit(data)

      const handled = handleResult(result, (errors) => setErrors(errors))

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
        onClose()
      }

      return handled
    },
  })

  useEffect(() => {
    track(trackingEvents.engagement.employeePulsePromptSeen, {
      employee_pulse_source: source,
    })
  }, [source])

  const onModalClose = () => {
    track(trackingEvents.engagement.employeePulsePromptIgnored, {
      employee_pulse_source: source,
    })
    onClose()
  }

  if (loading || !pulseQuestion) {
    return null
  }

  return (
    <Modal
      illustration={'surveys'}
      title={pulseQuestion.question}
      onClose={onModalClose}
      onSubmit={() => form.handleSubmit()}
      footer={
        <Button loading={form.isSubmitting} submit>
          {i18n.t('common.submit')}
        </Button>
      }
    >
      <Box gap="s16">
        <MoodSelector
          onChange={(value) => form.setFieldValue('value', value)}
          value={form.values.value}
          error={form.errors.value}
        />

        {form.values.value > 0 ? (
          <TextArea
            label={i18n.t('engagement.new_employee_pulse_modal.comment_label')}
            onChange={(value) => form.setFieldValue('comment', value)}
            value={form.values.comment}
            placeholder={i18n.t('engagement.new_employee_pulse_modal.comment_placeholder')}
          />
        ) : null}
      </Box>
    </Modal>
  )
}
